/**
 * Run `fn` over `items` with at most `limit` promises in flight at once.
 * Preserves input order. Never rejects — each result is a PromiseSettledResult,
 * same shape as Promise.allSettled.
 *
 * Use for batch operations (e.g. multi-file upload) so the client doesn't fire
 * N parallel requests at the backend and exhaust its small DB connection pool.
 */
export async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < items.length) {
      const i = cursor++;
      const item = items[i] as T; // i < items.length, so always defined
      try {
        results[i] = { status: "fulfilled", value: await fn(item, i) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  }

  const workers = Array.from(
    { length: Math.max(1, Math.min(limit, items.length)) },
    worker,
  );
  await Promise.all(workers);
  return results;
}
